<template>
  <div class="pie-chart-container">
    <div class="chart-title">{{ titulo }}</div>
    <div class="chart-wrapper">
      <div v-if="!hasValidData" class="no-data-message">
        Nenhum dado disponível para exibir
      </div>
      <PieChart v-else :height="400" :chartData="testData" :options="options" />
    </div>
  </div>
</template>

<script>
import { defineComponent, computed, ref } from "vue";
import { PieChart } from "vue-chart-3";
import { Chart, registerables } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

Chart.register(...registerables);
Chart.register(ChartDataLabels);

export default defineComponent({
  components: { PieChart },
  props: ["titulo", "labels", "datasets"],
  setup(props) {
    const formatarPreco = (valor) => {
      if (valor == null || valor === undefined) return "R$ 0,00";
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(parseFloat(valor));
    };

    const testData = computed(() => ({
      labels: props.labels || [],
      datasets: props.datasets || [],
    }));

    const hasValidData = computed(() => {
      return (
        props.labels &&
        props.labels.length > 0 &&
        props.datasets &&
        props.datasets.length > 0 &&
        props.datasets[0].data &&
        props.datasets[0].data.length > 0 &&
        props.datasets[0].data.some((value) => {
          const val = typeof value === "object" ? value.value : value;
          return val > 0;
        })
      );
    });

    const options = ref({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        datalabels: {
          display: true,
          color: "white",
          font: {
            weight: "bold",
            size: 12,
          },
          anchor: "end",
          align: "start",
          formatter: (value, context) => {
            const val = typeof value === "object" ? value.value : value;
            if (!val || isNaN(val) || val === 0) return "";

            const dataset = context.dataset;
            if (!dataset || !dataset.data || dataset.data.length === 0)
              return "";

            const total = dataset.data.reduce((sum, val) => {
              const num = typeof val === "object" ? val.value : val;
              return sum + (isNaN(num) ? 0 : parseFloat(num));
            }, 0);

            if (total === 0) return "";

            const percentage = (parseFloat(val) / total) * 100;

            if (percentage < 5) {
              return "";
            }

            return `${percentage.toFixed(1)}%`;
          },
        },
        legend: {
          position: "bottom",
          labels: {
            usePointStyle: true,
            pointStyle: "circle",
            padding: 20,
            font: {
              size: 12,
            },
            boxWidth: 15,
            boxHeight: 15,
            generateLabels: function (chart) {
              const data = chart.data;
              if (
                !data.labels ||
                !data.labels.length ||
                !data.datasets ||
                !data.datasets.length
              ) {
                return [];
              }

              const dataset = data.datasets[0];
              if (!dataset.data || dataset.data.length === 0) {
                return [];
              }

              const total = dataset.data.reduce((sum, val) => {
                const num = typeof val === "object" ? val.value : val;
                return sum + (isNaN(num) ? 0 : parseFloat(num));
              }, 0);

              if (total === 0) return [];

              return data.labels
                .map((label, i) => {
                  const value = dataset.data[i];
                  const numValue =
                    typeof value === "object" ? value.value : value;
                  const formatted =
                    typeof value === "object"
                      ? value.formatted
                      : formatarPreco(value);

                  if (isNaN(numValue) || numValue === 0) {
                    return null;
                  }

                  const percentage = ((numValue / total) * 100).toFixed(1);
                  
                  // limita o label em 40 caracteres
                  const truncatedLabel = label.length > 40 
                    ? label.substring(0, 40) + '...' 
                    : label;

                  return {
                    text: `${truncatedLabel}: ${formatted} (${percentage}%)`,
                    fillStyle: dataset.backgroundColor[i] || "#ccc",
                    strokeStyle: dataset.borderColor?.[i] || "#fff",
                    lineWidth: 2,
                    hidden: false,
                    index: i,
                  };
                })
                .filter((item) => item !== null);
            },
          },
        },
        title: {
          display: false,
        },
        tooltip: {
          enabled: true,
          callbacks: {
            label: function (context) {
              const rawValue = context.raw;

              const numValue =
                typeof rawValue === "object" ? rawValue.value : rawValue;
              const formatted =
                typeof rawValue === "object"
                  ? rawValue.formatted
                  : formatarPreco(rawValue);

              if (isNaN(numValue) || numValue === 0) return "";

              const dataset = context.dataset;
              if (!dataset || !dataset.data) return "";

              const total = dataset.data.reduce((sum, val) => {
                const num = typeof val === "object" ? val.value : val;
                return sum + (isNaN(num) ? 0 : parseFloat(num));
              }, 0);

              if (total === 0) return "";

              const percentage = ((numValue / total) * 100).toFixed(1);
              return `${context.label}: ${formatted} (${percentage}%)`;
            },
          },
        },
      },
      layout: {
        padding: {
          top: 10,
          bottom: 10,
          left: 10,
          right: 10,
        },
      },
    });

    return { testData, options, hasValidData };
  },
});
</script>

<style scoped>
.pie-chart-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 16px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 16px;
}

.chart-title {
  font-size: 16px;
  font-weight: bold;
  color: #21324a;
  text-align: center;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #f0f0f0;
}

.chart-wrapper {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
}

.no-data-message {
  color: #666;
  font-size: 14px;
  text-align: center;
  padding: 20px;
  background: #f5f5f5;
  border-radius: 8px;
  border: 1px dashed #ccc;
}

@media (max-width: 768px) {
  .pie-chart-container {
    margin-bottom: 24px;
  }

  .chart-title {
    font-size: 14px;
  }

  .chart-wrapper {
    min-height: 300px;
  }
}
</style>
